import { Injectable, signal, effect, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type ThemeMode = 'light' | 'dark' | 'system' | 'auto';
export type BackgroundStyle = 'india' | 'aurora' | 'sunset' | 'forest' | 'midnight' | 'vibrant';

export interface BackgroundTheme {
  id: BackgroundStyle;
  label: string;
  colors: string[];
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);
  
  theme = signal<ThemeMode>(this.getInitialTheme());
  backgroundStyle = signal<BackgroundStyle>(this.getInitialBackgroundStyle());

  backgroundThemes: BackgroundTheme[] = [
    { id: 'india', label: 'India', colors: ['bg-[#FF9933]', 'bg-[#FFFFFF]', 'bg-[#138808]', 'bg-[#000080]'] },
    { id: 'aurora', label: 'Aurora', colors: ['bg-ios-blue', 'bg-ios-purple', 'bg-ios-pink', 'bg-ios-teal'] },
    { id: 'sunset', label: 'Sunset', colors: ['bg-ios-orange', 'bg-ios-red', 'bg-ios-pink', 'bg-ios-indigo'] },
    { id: 'forest', label: 'Forest', colors: ['bg-ios-green', 'bg-ios-teal', 'bg-ios-blue', 'bg-ios-yellow'] },
    { id: 'midnight', label: 'Midnight', colors: ['bg-ios-indigo', 'bg-ios-purple', 'bg-ios-blue', 'bg-ios-indigo'] },
    { id: 'vibrant', label: 'Vibrant', colors: ['bg-ios-red', 'bg-ios-orange', 'bg-ios-yellow', 'bg-ios-pink'] }
  ];

  constructor() {
    effect(() => {
      const mode = this.theme();
      this.applyTheme(mode);
      if (this.isBrowser) {
        localStorage.setItem('kicgle-theme', mode);
      }
    });

    effect(() => {
      const style = this.backgroundStyle();
      if (this.isBrowser) {
        localStorage.setItem('kicgle-bg-style', style);
      }
    });

    if (this.isBrowser) {
      // Listen for system theme changes
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
        if (this.theme() === 'system') {
          this.applyTheme('system');
        }
      });
    }
  }

  setTheme(mode: ThemeMode) {
    this.theme.set(mode);
  }

  setBackgroundStyle(style: BackgroundStyle) {
    this.backgroundStyle.set(style);
  }

  private getInitialTheme(): ThemeMode {
    if (this.isBrowser) {
      const saved = localStorage.getItem('kicgle-theme') as ThemeMode;
      return saved || 'system';
    }
    return 'system';
  }

  private getInitialBackgroundStyle(): BackgroundStyle {
    if (this.isBrowser) {
      const saved = localStorage.getItem('kicgle-bg-style') as BackgroundStyle;
      return saved || 'india';
    }
    return 'india';
  }

  private applyTheme(mode: ThemeMode) {
    if (!this.isBrowser) return;

    let isDark = false;
    if (mode === 'dark') {
      isDark = true;
    } else if (mode === 'system') {
      isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    } else if (mode === 'auto') {
      const hour = new Date().getHours();
      isDark = hour < 6 || hour >= 18; // Dark mode from 6 PM to 6 AM
    }
    
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    this.updateStatusBarTheme(isDark);
  }

  private updateStatusBarTheme(isDark: boolean) {
    if (!this.isBrowser) return;

    const themeColor = isDark ? '#000000' : '#f2f2f7';
    const appleStatusStyle = isDark ? 'black-translucent' : 'default';

    // 1. Update or create primary theme-color meta tag
    let themeMeta = document.querySelector('meta[name="theme-color"]:not([media])') as HTMLMetaElement;
    if (!themeMeta) {
      themeMeta = document.createElement('meta');
      themeMeta.name = 'theme-color';
      document.head.appendChild(themeMeta);
    }
    themeMeta.content = themeColor;

    // 2. Update media-specific theme-color meta tags
    const lightThemeMeta = document.querySelector('meta[name="theme-color"][media*="light"]') as HTMLMetaElement;
    if (lightThemeMeta) {
      lightThemeMeta.content = themeColor;
    }

    const darkThemeMeta = document.querySelector('meta[name="theme-color"][media*="dark"]') as HTMLMetaElement;
    if (darkThemeMeta) {
      darkThemeMeta.content = themeColor;
    }

    // 3. Update iOS Apple mobile web app status bar style
    let appleMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]') as HTMLMetaElement;
    if (!appleMeta) {
      appleMeta = document.createElement('meta');
      appleMeta.name = 'apple-mobile-web-app-status-bar-style';
      document.head.appendChild(appleMeta);
    }
    appleMeta.content = appleStatusStyle;

    // 4. Update Windows tile color meta tag
    const msMeta = document.querySelector('meta[name="msapplication-TileColor"]') as HTMLMetaElement;
    if (msMeta) {
      msMeta.content = isDark ? '#000000' : '#007aff';
    }
  }
}
